import { InvalidBindingError, MissingServiceError, ResolutionCycleError } from "./error";
/**
 * Some libraries e.g. probe for .then on values they encounter (e.g. Jest), or look for $$typeof (e.g. React dev tools). For
 * those we'll silently return the underlying value (which is undefined).
 *
 * (We'll return undefined eventually in any case, but in these cases we do so with no warning)
 */
const defaultAllowedUndefinedValues = [
    "then",
    "$$typeof",
    // Cypress chainable probes these
    "jquery",
    "nodeType",
    "selector",
    "defaultView",
    "isSinonProxy",
    "host",
    "document",
    "inspect",
    "window",
    "length",
    "_spreadArray",
];
/**
 * Returns a Proxy object that offers lazy-initialization to a container-like object, from a set of defined factory methods
 *
 * This implements a simple lazy container contract:
 *  - Services are instantiated on first-use, via a "getter" hook
 *  - Services cause their dependencies to be instantiated, when their binding accesses further services
 *  - If a service tries to access a dependency that's already in the current initialization chain, we've hit a loop, and
 *    it means the services aren't defined in a DAG - we throw an error
 *
 * The main trick is that "proxy" holds a reference to itself, allowing it to act as the partially instantiated container
 * that is passed into the binding methods that create the services
 *
 * @tparam ServiceContainer         the public service container type
 * @tparam InternalServiceContainer always an extension of the public service container type, that also contains private
 *                                  services that shouldn't be able to be directly summoned, but only used to build other services
 * @tparam Context                  an optional context type that will be passed to each binding method as the second parameter
 */
export function withLazyInitialization(inputBindings, context, additionalAllowedUndefinedValues = []) {
    // An ordered chain of service names that are currently in the middle of being resolved
    const resolving = [];
    const bindings = inputBindings;
    const underlyingBaseContainer = {
        resolving,
        replaceBinding(key, binding, resetExistingInstance = false) {
            bindings[key] = binding;
            if (resetExistingInstance) {
                Reflect.deleteProperty(underlyingBaseContainer, key);
            }
        },
    };
    const allowedUndefinedValues = new Set([...defaultAllowedUndefinedValues, ...additionalAllowedUndefinedValues]);
    const proxy = new Proxy(underlyingBaseContainer, {
        get(target, key) {
            if (typeof key === "string" && target[key] === undefined && !allowedUndefinedValues.has(key)) {
                // Check if we are already resolving the service's key, which would indicate an infinite cycle of dependencies
                if (resolving.indexOf(key) !== -1) {
                    throw new ResolutionCycleError(key, resolving);
                }
                const binding = bindings?.[key];
                // Malformed or missing binding
                if (typeof binding !== "function") {
                    // eslint-disable-next-line no-console
                    console.warn(typeof binding === "undefined"
                        ? new MissingServiceError(key)
                        : new InvalidBindingError(key));
                    return Reflect.get(target, key, proxy);
                }
                // If the binding is a factory function we'll resolve it, perhaps recursively (by passing ourselves in
                //   to the factory method)
                try {
                    resolving.push(key);
                    target[key] = binding(proxy, context); // Here's the recursive step
                }
                finally {
                    resolving.pop();
                }
            }
            return Reflect.get(target, key, proxy);
        },
    });
    return proxy;
}
//# sourceMappingURL=init.js.map